// client/src/hooks/useSummaryMesh.js
// Итерация 9.6: загрузка summary_mesh с world_manager (:4002).
//
// ⚠️ Использует Gateway.worldManager (baseURL :4002), НЕ Gateway.api.*.

import { useState, useEffect, useRef } from 'react';
import Gateway from '../api/gateway';

/**
 * Хук для загрузки summary_mesh структуры (Ship, Station, Military, Building, Settlement).
 *
 * @param {string} type - тип структуры: "ship" | "station" | "military_structure" | "building" | "settlement"
 * @param {string} id - ID структуры
 * @param {object} [options]
 * @param {number} [options.ttlMs=3600000] - TTL кэша в миллисекундах
 * @param {boolean} [options.enabled=true] - если false, запрос не выполняется
 *
 * @returns {{ mesh: object | null, loading: boolean, error: Error | null, reload: () => void }}
 */
export function useSummaryMesh(type, id, options = {}) {
  const { ttlMs = 60 * 60 * 1000, enabled = true } = options;

  const [mesh, setMesh] = useState(() => getCached(type, id, ttlMs));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [reloadCounter, setReloadCounter] = useState(0);

  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!enabled || !type || !id) {
      setLoading(false);
      return;
    }

    const cached = getCached(type, id, ttlMs);
    if (cached) {
      setMesh(cached);
      setError(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    fetchSummary(type, id)
      .then((summaryMesh) => {
        if (cancelled || !mountedRef.current) return;
        cacheSet(type, id, summaryMesh);
        setMesh(summaryMesh);
        setError(null);
      })
      .catch((err) => {
        if (cancelled || !mountedRef.current) return;
        // eslint-disable-next-line no-console
        console.error(`[useSummaryMesh] ${type}/${id} error:`, err);
        setError(err instanceof Error ? err : new Error(String(err)));
        setMesh(null);
      })
      .finally(() => {
        if (cancelled || !mountedRef.current) return;
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [type, id, enabled, ttlMs, reloadCounter]);

  const reload = () => {
    invalidateCache(type, id);
    setReloadCounter((n) => n + 1);
  };

  return { mesh, loading, error, reload };
}

/**
 * Универсальный хук для любого типа (vehicle, drone, module, scheme).
 *
 * @param {string} endpoint - относительный путь, например "/meshes/vehicle/123"
 * @param {object} [options]
 * @returns {{ mesh: object | null, loading: boolean, error: Error | null, reload: () => void }}
 */
export function useMesh(endpoint, options = {}) {
  const { ttlMs = 60 * 60 * 1000, enabled = true } = options;

  const [mesh, setMesh] = useState(() => getCachedByEndpoint(endpoint, ttlMs));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [reloadCounter, setReloadCounter] = useState(0);

  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!enabled || !endpoint) {
      setLoading(false);
      return;
    }

    const cached = getCachedByEndpoint(endpoint, ttlMs);
    if (cached) {
      setMesh(cached);
      setError(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    fetchEndpoint(endpoint)
      .then((data) => {
        if (cancelled || !mountedRef.current) return;
        cacheSetByEndpoint(endpoint, data);
        setMesh(data);
        setError(null);
      })
      .catch((err) => {
        if (cancelled || !mountedRef.current) return;
        // eslint-disable-next-line no-console
        console.error(`[useMesh] ${endpoint} error:`, err);
        setError(err instanceof Error ? err : new Error(String(err)));
        setMesh(null);
      })
      .finally(() => {
        if (cancelled || !mountedRef.current) return;
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [endpoint, enabled, ttlMs, reloadCounter]);

  const reload = () => {
    invalidateCacheByEndpoint(endpoint);
    setReloadCounter((n) => n + 1);
  };

  return { mesh, loading, error, reload };
}

// ============================================================================
// INTERNAL — FETCH
// ============================================================================

async function fetchSummary(type, id) {
  const key = cacheKey(type, id);

  if (INFLIGHT.has(key)) return INFLIGHT.get(key);

  const promise = (async () => {
    try {
      // 🔥 world_manager (baseURL :4002)
      const response = await Gateway.worldManager.get(
        `/api/meshes/structure/${type}/${id}/summary`
      );
      const data = response?.data || response;
      const mesh = data?.summary_mesh || data?.mesh || data;
      if (!mesh) {
        throw new Error(`Empty summary_mesh for ${type}/${id}`);
      }
      return mesh;
    } finally {
      INFLIGHT.delete(key);
    }
  })();

  INFLIGHT.set(key, promise);
  return promise;
}

async function fetchEndpoint(endpoint) {
  const key = endpointKey(endpoint);

  if (INFLIGHT.has(key)) return INFLIGHT.get(key);

  const promise = (async () => {
    try {
      const response = await Gateway.worldManager.get(`/api${endpoint}`);
      const data = response?.data || response;
      return data?.mesh || data?.summary_mesh || data;
    } finally {
      INFLIGHT.delete(key);
    }
  })();

  INFLIGHT.set(key, promise);
  return promise;
}

// ============================================================================
// INTERNAL — CACHE
// ============================================================================

const CACHE = new Map();
const INFLIGHT = new Map();

function cacheKey(type, id) {
  return `${type}:${id}`;
}

function endpointKey(endpoint) {
  return `endpoint:${endpoint}`;
}

function getCached(type, id, ttlMs) {
  if (!type || !id) return null;
  const key = cacheKey(type, id);
  const entry = CACHE.get(key);
  if (!entry) return null;
  if (Date.now() - entry.timestamp > ttlMs) {
    CACHE.delete(key);
    return null;
  }
  return entry.value;
}

function cacheSet(type, id, value) {
  CACHE.set(cacheKey(type, id), { value, timestamp: Date.now() });
}

function invalidateCache(type, id) {
  if (!type || !id) return;
  CACHE.delete(cacheKey(type, id));
  INFLIGHT.delete(cacheKey(type, id));
}

function getCachedByEndpoint(endpoint, ttlMs) {
  if (!endpoint) return null;
  const key = endpointKey(endpoint);
  const entry = CACHE.get(key);
  if (!entry) return null;
  if (Date.now() - entry.timestamp > ttlMs) {
    CACHE.delete(key);
    return null;
  }
  return entry.value;
}

function cacheSetByEndpoint(endpoint, value) {
  if (!endpoint) return;
  CACHE.set(endpointKey(endpoint), { value, timestamp: Date.now() });
}

function invalidateCacheByEndpoint(endpoint) {
  if (!endpoint) return;
  CACHE.delete(endpointKey(endpoint));
  INFLIGHT.delete(endpointKey(endpoint));
}

// ============================================================================
// PUBLIC UTILS
// ============================================================================

export function clearMeshCache() {
  CACHE.clear();
  INFLIGHT.clear();
}

export function invalidateMesh(type, id) {
  invalidateCache(type, id);
}

export function meshCacheSize() {
  return CACHE.size;
}
